// ─────────────────────────────────────────────────────────────────────
// nativeBridge — how the web member app talks to the native Expo shell
// (apps/mobile) when it runs inside that shell's WebView.
//
// The SAME message types live in apps/mobile/src/bridge.ts. There is no shared
// package yet (it would have to be copied into the web Docker image), so keep
// the two files in sync by hand; both sides have tests pinning the `type`
// strings and the event name. Protocol version: BRIDGE_VERSION.
//
// Transport:
//   web → native   window.ReactNativeWebView.postMessage(JSON.stringify(msg))
//   native → web   a `starpoint:native` CustomEvent on `window` (detail = msg)
//
// Detection is synchronous: the shell loads the SPA with `?native=1` and also
// sets `window.__STARPOINT_NATIVE__` before the page runs, so the app can pick
// the standalone layout on the very first render.
// ─────────────────────────────────────────────────────────────────────

export const BRIDGE_VERSION = 1;
export const NATIVE_EVENT = 'starpoint:native';

export type PushPermission = 'granted' | 'denied' | 'undetermined' | 'unavailable';

/** Messages the WEB app sends to the native shell. */
export type WebToNative =
  | { type: 'ready'; version?: string }
  | { type: 'getPushStatus' }
  | { type: 'requestPushPermission' }
  | { type: 'openExternal'; url: string };

/** Messages the native shell sends to the WEB app. */
export type NativeToWeb =
  | { type: 'native'; platform: 'ios' | 'android'; appVersion: string; bridgeVersion: number }
  | {
      type: 'pushStatus';
      status: PushPermission;
      platform: 'ios' | 'android';
      token?: string;
      error?: string;
    }
  | { type: 'notificationOpened'; data: Record<string, unknown> };

export interface NativeInfo {
  platform: 'ios' | 'android';
  appVersion: string;
  bridgeVersion: number;
}

type BridgeWindow = Window & {
  ReactNativeWebView?: { postMessage: (data: string) => void };
  __STARPOINT_NATIVE__?: NativeInfo;
};

const win = (): BridgeWindow | null =>
  typeof window === 'undefined' ? null : (window as BridgeWindow);

/** True when running inside the native shell's WebView (the bridge exists). */
export function isNativeShell(): boolean {
  const w = win();
  return !!w && (!!w.ReactNativeWebView || !!w.__STARPOINT_NATIVE__);
}

/**
 * Pure: is `hostname` this machine? Used to keep the pad pairing screen usable
 * during development without opening it to the public web.
 */
export function isLocalHostname(hostname: string | null | undefined): boolean {
  const h = String(hostname || '')
    .trim()
    .toLowerCase()
    .replace(/^\[|\]$/g, '');
  return h === 'localhost' || h === '127.0.0.1' || h === '::1' || h.endsWith('.localhost');
}

/**
 * May THIS client pair a Banepad device?
 *
 * Pairing is restricted to the Banepad kiosk app (apps/mobile), which the club
 * installs through Intune. That keeps the public `/pad` route from binding a
 * random browser to a court, and stops staff pairing a browser tab by mistake.
 * `localhost` stays open so the route can still be developed and tested.
 *
 * ⚠️ This is a DETERRENT, not a security control — the marker it reads can be
 * set from a browser console. Real device security is server-side pairing with
 * hashed tokens; see docs/banepad/device-security.md, scheduled with the
 * Supabase → Postgres migration. Already-paired pads are unaffected either way.
 */
export function canPairBanepadDevice(
  hostname: string | null | undefined = win()?.location.hostname,
): boolean {
  // There used to be a third way in: a build flag that let an ordinary browser
  // pair on the temporary #216 rehearsal Web App. That app is deleted and the
  // flag with it (#997), so no build can switch this deterrent off.
  return isNativeShell() || isLocalHostname(hostname);
}

/** What the shell told us about itself before the page loaded (if anything). */
export function nativeInfo(): NativeInfo | null {
  return win()?.__STARPOINT_NATIVE__ ?? null;
}

/**
 * Pure: should the member app render STANDALONE (full viewport, no phone
 * frame, no desktop header)? Yes inside the native shell, or when the URL
 * carries `native=1` — which also lets a plain browser preview that layout.
 */
export function isStandaloneMobile(search: string = win()?.location.search ?? ''): boolean {
  if (isNativeShell()) return true;
  try {
    return new URLSearchParams(search).get('native') === '1';
  } catch {
    return false;
  }
}

/** Send a message to the shell. Returns false (and does nothing) outside it. */
export function postToNative(msg: WebToNative): boolean {
  const w = win();
  if (!w?.ReactNativeWebView) return false;
  try {
    w.ReactNativeWebView.postMessage(JSON.stringify(msg));
    return true;
  } catch {
    return false;
  }
}

/** Pure: is this CustomEvent detail one of the shell's messages? */
export function parseNativeMessage(detail: unknown): NativeToWeb | null {
  if (!detail || typeof detail !== 'object') return null;
  const t = (detail as { type?: unknown }).type;
  if (t === 'native' || t === 'pushStatus' || t === 'notificationOpened') {
    return detail as NativeToWeb;
  }
  return null;
}

/** Subscribe to messages from the shell. Returns the unsubscribe function. */
export function onNativeMessage(handler: (msg: NativeToWeb) => void): () => void {
  const w = win();
  if (!w) return () => {};
  const listener = (e: Event) => {
    const msg = parseNativeMessage((e as CustomEvent).detail);
    if (msg) handler(msg);
  };
  w.addEventListener(NATIVE_EVENT, listener);
  return () => w.removeEventListener(NATIVE_EVENT, listener);
}
